import { InfoCircleIcon, LinkBrokenBIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Input, overlayStyle, Select, Stack, Switch, Text, Tooltip, Uploader } from '@prophecy/ui-v3';
import { message } from '@prophecy/ui-v3/LegacyToast';
import React, { useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { ProviderType } from '../../../../data/apis/types';
import { IdpProviderLabelMap } from '../../config/admin';
import {
  certificateStatusFor,
  generateAndDownloadCertificate,
  generateTooltipFor,
  signingTooltipFor
} from './logoutSigning';
import ProphecyManaged, { SCIMField } from './ProphecyManaged';
import { ScrollableStack } from './styled';
import { ComponentProps } from './types';

const StyledUploader = styled(Uploader)`
  margin: 0;
  padding: 0;
  > div {
    justify-content: flex-start;
  }
`;

export default function SamlAuth(props: ComponentProps) {
  const { control, errors, isSCIMV1, savedSloEnabled, signingConnectorId, hasLogoutSigningKey, refreshSigningState } =
    props;
  const [isGenerating, setIsGenerating] = useState(false);

  // The URL requirement follows the form; the rest follows saved state, because generating a
  // certificate reaches the server before Save.
  const isSloEnabled = Boolean(useWatch({ control, name: 'saml.sloEnabled' }));
  const isSloSaved = Boolean(savedSloEnabled);
  const hasCertificate = Boolean(hasLogoutSigningKey);
  const canSignLogout = isSloSaved && hasCertificate;
  const canGenerate = isSloSaved && Boolean(signingConnectorId);

  const onGenerateCertificate = async () => {
    if (!signingConnectorId) return;

    setIsGenerating(true);
    const error = await generateAndDownloadCertificate(signingConnectorId);
    setIsGenerating(false);

    if (error) {
      message.error({ content: error });
      return;
    }
    message.success({ content: 'Signature certificate generated and downloaded.' });
    refreshSigningState?.();
  };
  const uploadFiles = (files: Blob[], onChange: (e: unknown) => void) => {
    let reader = new FileReader();
    const uploadedFile = files[0];
    reader.readAsText(uploadedFile);
    reader.onloadend = () => {
      onChange(reader.result);
    };
  };
  return (
    <ScrollableStack gap={theme.spaces.x24}>
      {/*<Controller
        render={({ field }) => (
          <UserPrompt
            hasPrompt={!props.isSCIMEnabled || props.isSCIMV1}
            errorMsg={errors?.saml?.usernamePrompt?.message}
            value={field.value}
            onChange={field.onChange}
          />
        )}
        control={control}
        name='saml.usernamePrompt'
      />*/}
      {isSCIMV1 && <SCIMField {...props} isSAML />}
      <Controller
        render={({ field }) => (
          <Select
            label='IDP'
            {...field}
            required
            options={Object.keys(ProviderType).map((value) => ({ label: IdpProviderLabelMap?.[value], value }))}
            variant={errors.idp ? 'error' : 'primary'}
            helpText={errors.idp?.message}
            data-test-id='saml-auth-idp-select'
          />
        )}
        control={control}
        name='idp'
        rules={{
          required: 'IDP Provider is required'
        }}
      />
      {!isSCIMV1 && <ProphecyManaged {...props} isSAML />}
      <Controller
        render={({ field }) => (
          <Input
            label={
              <Stack gap={theme.spaces.x4} direction='horizontal'>
                SSO URL
                <Tooltip title='SSO URL generated by Okta.'>
                  <div>
                    <InfoCircleIcon type='default' color={theme.colors.gray500} />
                  </div>
                </Tooltip>
              </Stack>
            }
            {...field}
            required
            variant={errors?.saml?.ssoURL ? 'error' : 'primary'}
            helpText={errors?.saml?.ssoURL?.message}
            data-test-id='saml-auth-sso-url-input'
          />
        )}
        control={control}
        name='saml.ssoURL'
        rules={{
          required: 'URL is required'
        }}
      />
      <Stack direction='horizontal' align='space-between' alignY='center'>
        <Controller
          render={({ field }) => (
            <StyledUploader showUploadList={true} maxFiles={1} onDrop={(files) => uploadFiles(files, field.onChange)}>
              <Stack gap={theme.spaces.x12} align='space-between' alignY='center' direction='horizontal'>
                <Button
                  icon={<LinkBrokenBIcon type='default' />}
                  variant='secondaryGray'
                  data-test-id='saml-auth-certificate-button'>
                  Certificate
                </Button>
                <Tooltip
                  overlayStyle={overlayStyle}
                  placement='right'
                  title='Certificate to use when validating the signature of the SAML response.'>
                  <div>
                    <InfoCircleIcon type='default' color={theme.colors.gray500} />
                  </div>
                </Tooltip>
              </Stack>
            </StyledUploader>
          )}
          control={control}
          name='saml.caData'
        />
        <Controller
          render={({ field }) => (
            <Stack gap={theme.spaces.x4} direction='horizontal' alignY='center'>
              <Switch {...field} checked={Boolean(field.value)} data-test-id='saml-auth-skip-cert-verification-switch'>
                * Skip certificate verification
              </Switch>
              <Tooltip
                overlayStyle={overlayStyle}
                title='To skip signature validation, uncomment the following field. This should only be used during testing and may be removed in the future.'>
                <div>
                  <InfoCircleIcon type='default' color={theme.colors.gray500} />
                </div>
              </Tooltip>
            </Stack>
          )}
          control={control}
          name='saml.insecureSkipSignatureValidation'
        />
      </Stack>
      <Stack direction='horizontal' align='space-between' gap={theme.spaces.x12} width='100%'>
        <Stack width='50%'>
          <Controller
            render={({ field }) => (
              <Input
                label={
                  <Stack gap={theme.spaces.x4} direction='horizontal'>
                    * Entity issuer
                    <Tooltip
                      overlayStyle={overlayStyle}
                      title='A unique Okta Entity ID is generated for each application.'>
                      <div>
                        <InfoCircleIcon type='default' color={theme.colors.gray500} />
                      </div>
                    </Tooltip>
                  </Stack>
                }
                {...field}
                variant={errors?.saml?.entityIssuer ? 'error' : 'primary'}
                helpText={errors?.saml?.entityIssuer?.message}
                data-test-id='saml-auth-entity-issuer-input'
              />
            )}
            control={control}
            name='saml.entityIssuer'
            rules={{
              required: 'Entity Issuer is required'
            }}
          />
        </Stack>
        <Stack width='50%'>
          <Controller
            render={({ field }) => (
              <Input
                label={
                  <Stack gap={theme.spaces.x4} direction='horizontal'>
                    * SSO issuer
                    <Tooltip title='Issuer value expected in the SAML response.'>
                      <div>
                        <InfoCircleIcon type='default' color={theme.colors.gray500} />
                      </div>
                    </Tooltip>
                  </Stack>
                }
                {...field}
                variant={errors?.saml?.ssoIssuer ? 'error' : 'primary'}
                helpText={errors?.saml?.ssoIssuer?.message}
                data-test-id='saml-auth-sso-issuer-input'
              />
            )}
            control={control}
            name='saml.ssoIssuer'
            rules={{
              required: 'SSO Issuer is required'
            }}
          />
        </Stack>
      </Stack>
      <Controller
        render={({ field }) => (
          <Stack gap={theme.spaces.x4} direction='horizontal' alignY='center'>
            <Switch {...field} checked={Boolean(field.value)} data-test-id='saml-auth-slo-enabled-switch'>
              Enable Single Logout
            </Switch>
            <Tooltip
              overlayStyle={overlayStyle}
              title='When on, signing out of Prophecy also signs the user out of your identity provider'>
              <div>
                <InfoCircleIcon type='default' color={theme.colors.gray500} />
              </div>
            </Tooltip>
          </Stack>
        )}
        control={control}
        name='saml.sloEnabled'
      />
      <Controller
        render={({ field }) => (
          <Input
            label={
              <Stack gap={theme.spaces.x4} direction='horizontal'>
                Single Logout URL
                <Tooltip
                  overlayStyle={overlayStyle}
                  title="Your identity provider's Single Logout endpoint, from its metadata. Required to enable Single Logout.">
                  <div>
                    <InfoCircleIcon type='default' color={theme.colors.gray500} />
                  </div>
                </Tooltip>
              </Stack>
            }
            {...field}
            value={field.value ?? ''}
            disabled={!isSloEnabled}
            required={isSloEnabled}
            variant={errors?.saml?.ssoLogoutURL ? 'error' : 'primary'}
            helpText={errors?.saml?.ssoLogoutURL?.message}
            data-test-id='saml-auth-slo-url-input'
          />
        )}
        control={control}
        name='saml.ssoLogoutURL'
        rules={{
          // Conditional rather than unmounting, which would strand the error in formState.
          required: isSloEnabled ? 'Single Logout URL is required' : undefined
        }}
      />
      <Stack direction='horizontal' alignY='center' gap={theme.spaces.x16}>
        <Controller
          render={({ field }) => (
            <Switch
              {...field}
              // Never show it on when it cannot work; the saved value is left alone.
              checked={canSignLogout && Boolean(field.value)}
              disabled={!canSignLogout}
              tooltip={signingTooltipFor(isSloSaved, hasCertificate)}
              data-test-id='saml-auth-sign-logout-switch'>
              Sign single logout (SLO) messages
            </Switch>
          )}
          control={control}
          name='saml.signLogoutRequests'
        />
        <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8}>
          <Button
            size='s'
            variant='secondaryGray'
            disabled={!canGenerate}
            loading={isGenerating}
            tooltip={generateTooltipFor(isSloSaved)}
            onClick={onGenerateCertificate}
            data-test-id='saml-auth-generate-signing-cert-button'>
            Generate Signature Certificate
          </Button>
          <Text level='xs' tone={theme.colors.gray500} data-test-id='saml-auth-signing-cert-status'>
            {certificateStatusFor(isSloSaved, hasCertificate)}
          </Text>
        </Stack>
      </Stack>
    </ScrollableStack>
  );
}
